/**
 * Guardrails Adapter
 * Transforms guardrail data between API and application layers
 */

import { BaseAdapter } from './base.adapter';
import type {
  Guardrail,
  GuardrailFallback,
  GuardrailStats,
  GuardrailFallbackStats,
  GuardrailResponseDto,
  GuardrailFallbackResponseDto,
  GuardrailStatsResponseDto,
  GuardrailFallbackStatsResponseDto,
  CreateGuardrailDto,
  UpdateGuardrailDto,
  CreateGuardrailFallbackDto
} from '@/types';

export class GuardrailAdapter extends BaseAdapter {
  static fromApi(dto: GuardrailResponseDto): Guardrail {
    return {
      id: dto.id,
      name: dto.name,
      description: dto.description,
      tags: dto.tags,
      status: dto.status,
      type: dto.type,
      createdAt: dto.createdAt,
      updatedAt: dto.updatedAt,
      createdBy: dto.createdBy
    };
  }

  static fromApiList(dtos: GuardrailResponseDto[]): Guardrail[] {
    return dtos.map((dto) => this.fromApi(dto));
  }

  static toCreateRequest(data: CreateGuardrailDto): CreateGuardrailDto {
    return this.sanitizePayload({
      name: data.name.trim(),
      description: data.description.trim(),
      tags: data.tags,
      status: data.status
    });
  }

  static toUpdateRequest(data: UpdateGuardrailDto): UpdateGuardrailDto {
    return this.sanitizePayload({
      ...(data.name !== undefined && { name: data.name.trim() }),
      ...(data.description !== undefined && { description: data.description.trim() }),
      ...(data.tags !== undefined && { tags: data.tags }),
      ...(data.status !== undefined && { status: data.status })
    });
  }

  static fromStatsApi(dto: GuardrailStatsResponseDto): GuardrailStats {
    return {
      total: dto.total,
      activeCount: dto.activeCount,
      inactiveCount: dto.inactiveCount,
      systemCount: dto.systemCount,
      customCount: dto.customCount
    };
  }
}

export class GuardrailFallbackAdapter extends BaseAdapter {
  static fromApi(dto: GuardrailFallbackResponseDto): GuardrailFallback {
    return {
      id: dto.id,
      message: dto.message,
      createdBy: dto.createdBy,
      createdAt: dto.createdAt,
      updatedAt: dto.updatedAt
    };
  }

  static fromApiList(dtos: GuardrailFallbackResponseDto[]): GuardrailFallback[] {
    return dtos.map((dto) => this.fromApi(dto));
  }

  static toCreateRequest(data: CreateGuardrailFallbackDto): CreateGuardrailFallbackDto {
    return this.sanitizePayload({
      message: data.message.trim()
    });
  }

  static fromStatsApi(dto: GuardrailFallbackStatsResponseDto): GuardrailFallbackStats {
    return {
      total: dto.total,
      activeFallbackCount: dto.activeFallbackCount
    };
  }
}
